import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CFlatList } from '@app/components/Common/';

import { TradeDetailListSection } from './styles';

import TradeDetailListHeader from './TradeDetailListHeader';
import TradeDetailListItem from './TradeDetailListItem';
import TradeDetailError from './TradeDetailError';

import { useFetcher } from '@app/hooks/fetcher';
import { AuthCheckText } from '@app/components';
import { useToken } from '@app/hooks';
import { TradeText } from '@app/organisms/Trade/TradeDetail/TradeFilledTable/styles.tsx';
import { Text } from '@/styles/reactnative.tsx';
import BigNumber from 'bignumber.js';
import { COLORS } from '@app/constants';
import KrwPrice from '@components/KrwPrice.tsx';
import EntryTime from '@components/EntryTime.tsx';
import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatDecimal4Truncate } from '@utils/formatDecimal.tsx';
import { truncateWithComma } from '@utils/Truncate.ts';
import EmptyMessage from '@/app/components/EmptyMessage';
import useScrollLayout from '../useScrollLayout';
import { useExchangeStore } from '@/app/hooks/trade/exchangeStore';
import ContractsColumn from '../Columns/ContractsColumn';
import dayjs from 'dayjs';

const TradeOrderHistoryTable = ({ parentHeight }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: token } = useToken();

  const { t } = useTranslation();
  const { EPs } = useExchangeStore();

  const KEYURL = '/trade/orderlist?status=*&ep_id=*';

  const { data, isError } = useFetcher(KEYURL, false);

  const list = data?.result?.result?.list;
  const { headerHeight, needsScroll } = useScrollLayout(
    containerRef,
    list,
    parentHeight
  );

  // const dataType = [
  //   "contracts",
  //   "amount",
  //   "orderPrice",
  //   "tradeType",
  //   "orderType",
  //   "margin",
  //   "fee",
  //   "orderTime",
  //   "status",
  // ];

  const columns = [
    {
      width: '11.1%',
      dataIndex: '',
      title: t('contracts'),
      render: (value, data) => (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
          <ContractsColumn item={data} />
        </div>
      ),
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: t('amount'),
      render: (value, data) => {
        return (
          <TradeText style={{ justifyContent: 'flex-start' }}>
            {BigNumber(data?.amount).multipliedBy(data?.leverage).toFormat(3)}
          </TradeText>
        );
      },
    },
    // {
    //   dataIndex: '',
    //   title: t('filledPrice'),
    //   render: (value, data) => {
    //     const currency = data?.pairs?.split("/")[1];
    //     return (
    //       <TradeText style={{whiteSpace: 'nowrap', justifyContent: 'flex-start'}}>
    //         {data?.trade_price || data?.order_price} {currency}
    //       </TradeText>
    //     );
    //   },
    // },
    {
      width: '11.1%',
      dataIndex: '',
      title: t('orderPrice'),
      render: (value, data) => {
        const EP = EPs?.find((ep: any) => +ep.id === +data?.exchange_pairs_id);
        const currency = data?.pairs?.split('/')[1];
        const dp = EP?.dp || 3;

        return (
          <TradeText
            style={{ whiteSpace: 'nowrap', justifyContent: 'flex-start' }}
          >
            {data?.limit_market !== 'M'
              ? `${BigNumber(data?.order_price).toFormat(dp)} ${currency}`
              : '-'}
          </TradeText>
        );
      },
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: t('conclusionPrice'),
      render: (value, data) => {
        const EP = EPs?.find((ep: any) => +ep.id === +data?.exchange_pairs_id);
        const currency = data?.pairs?.split('/')[1];
        const dp = EP?.dp || 3;

        return (
          <TradeText
            style={{ whiteSpace: 'nowrap', justifyContent: 'flex-start' }}
          >
            {(data?.status === 1 || data?.status === 2) && data?.trade_price
              ? `${BigNumber(data?.trade_price).toFormat(dp)} ${currency}`
              : '-'}
          </TradeText>
        );
      },
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: t('orderType'),
      render: (value, data) => {
        const timeInForce = data?.time_in_force;
        const gtdExpiry = data?.gtd_expiry;
        return (
          <TradeText
            style={{ whiteSpace: 'nowrap', justifyContent: 'flex-start', flexDirection: 'column', alignItems: 'flex-start' }}
          >
            <Text>
              {data?.limit_market === 'L' ? t('limit') : t('market')}{' '}
              {data?.data?.is_triggered && ` (${t('trigger')})`}
            </Text>
            {/* Time In Force 표시 (지정가 주문일 때만) */}
            {data?.limit_market === 'L' && timeInForce && (
              <Text style={{ fontSize: 11, color: timeInForce === 'GTD' ? COLORS.primary : COLORS.lightGray }}>
                {timeInForce === 'GTD' && gtdExpiry
                  ? `GTD ${dayjs(gtdExpiry).format('MM/DD HH:mm')}`
                  : timeInForce}
              </Text>
            )}
          </TradeText>
        );
      },
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: t('margin'),
      render: (value, data) => {
        const currency = data?.pairs?.split('/')[1];
        return (
          <TradeText
            style={{ whiteSpace: 'nowrap', justifyContent: 'flex-start' }}
          >
            {truncateWithComma(data?.margin, 3)} {currency}
          </TradeText>
        );
      },
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: t('fee'),
      render: (value, data) => {
        return (
          <TradeText style={{ justifyContent: 'flex-start', width: 'auto', minWidth: 80 }}>
            <KrwPrice
              dp={3}
              price={formatDecimal4Truncate(
                -BigNumber(data?.fee_price).toNumber()
              )}
              krw={true}
              fontSize={14}
            />
          </TradeText>
        );
      },
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: t('orderTime'),
      render: (value, data) => {
        return (
          <EntryTime
            style={{ justifyContent: 'left' }}
            time={data?.created_at}
          />
        );
      },
    },
    {
      width: '11.1%',
      dataIndex: '',
      title: t('status'),
      render: (value, data) => {
        return (
          <div style={{}}>
            {data?.status === 1
              ? t('opened')
              : data?.status === 2
                ? t('filled')
                : t('canceled')}
          </div>
        );
      },
    },
  ];

  // const renderItem = (item: any, index: any) => {
  //   return (
  //     <TradeDetailListItem
  //       item={item}
  //       index={index}
  //       dataType={dataType.length}
  //     />
  //   );
  // };

  return (
    <TradeDetailListSection ref={containerRef}>
      {/*<TradeDetailListHeader dataType={dataType} />*/}
      {isError ? <TradeDetailError /> : null}

      {token ? null : <AuthCheckText />}

      {list?.length ? (
        <Table
          columns={columns}
          dataSource={list}
          pagination={false}
          style={{ width: '100%', height: '100%' }}
          sticky={true}
          size="small"
          scroll={
            needsScroll
              ? {
                  x: 'max-content',
                  y: parentHeight - (headerHeight || 38) - 20, // 실제 헤더 높이 + 여백
                }
              : {
                  x: 'max-content',
                }
          }
        />
      ) : (
        <EmptyMessage />
      )}
      {/*{list?.length ? <CFlatList data={list} renderItem={renderItem} /> : null}*/}
    </TradeDetailListSection>
  );
};

export default TradeOrderHistoryTable;
